'use client';

import { useQuery } from '@tanstack/react-query';

import { mapFavoriteClubsData } from '@/modules/players/v2/tabs/clubs/utils';
import { ORGANIZATION_MODULE_FOR } from '@/modules/players/v2/tabs/constants';
import { ClubTypes } from '@/types/clubs.types';
import { SearchParams } from '@/types/types';
import { getPlayerClubs } from '@/utils/actions/players/non-edit/getPlayerClubs';
import { getFavoriteClubs } from '@/utils/actions/players/non-edit/getPlayerClubsFavorites';

const PLAYER_CLUBS_QUERY_KEY = 'player-clubs';

interface UsePlayerClubsArgs {
  playerId: string;
  activeClubType: ClubTypes;
  page: string;
  queryParams?: SearchParams;
}

export const usePlayerClubs = ({
  playerId,
  activeClubType,
  page,
  queryParams,
}: UsePlayerClubsArgs) => {
  const isFavorites = activeClubType === ClubTypes.Favorites;

  return useQuery({
    queryKey: [PLAYER_CLUBS_QUERY_KEY, playerId, activeClubType, page, queryParams],

    queryFn: async () => {
      if (isFavorites) {
        const res = await getFavoriteClubs({
          userUuid: playerId,
          module: ORGANIZATION_MODULE_FOR,
          queryParams,
        });

        return {
          error: res.error,
          clubs: mapFavoriteClubsData(res?.data?.results?.modules || []),
          totalRecords: res?.data?.totalRecords || 0,
        };
      }

      const type = activeClubType === ClubTypes.MemberOf ? 'member' : 'manager';

      const res = await getPlayerClubs({
        id: playerId,
        type,
        page: Number(page),
        queryParams,
      });

      return {
        error: res.error,
        clubs: res?.data?.clubs || [],
        totalRecords: res?.data?.totalRecords || 0,
      };
    },

    keepPreviousData: true,
  });
};
